import { HttpStatusCode } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { dateFns } from '@app/core/date-utils';
import { ProblemExistingCustomerIdAndSsnMismatch } from '@app/core/http-interceptors/http-error.interceptor';
import { transformAddress } from '@app/core/utils';
import { CustomerErrorCode } from '@app/sales/constants/ProblemCodes';
import { salesConstants } from '@app/sales/constants/sales-constants';
import { VerificationService } from '@app/sales/services/verification.service';
import { UpdateCustomerAllowanceFormAction } from '@app/sales/states/form/form.actions';
import { BankIdStatusRequest } from '@app/shared/models/bank-id-status-request.model';
import { BankIdStatusResponse } from '@app/shared/models/bank-id-status-response.model';
import { InFlightOrder } from '@core/models/in-flight-order.model';
import { AuthorizeResult, BankIdVerification, BankIdVerificationStatus } from '@core/models/initialize-response.model';
import { AppInsightsService } from '@core/services/application-insights.service';
import { NotificationService } from '@core/services/notification.service';
import { Action, State, StateContext, Store } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { CreateAuthorizedShoppingCartRequest } from '@sales/models/order-capture.model';
import { SetExistingVoiceSubscriptionsAndAddNewSubscriptionsAction } from '@sales/states/customer-product/customer-product.actions';
import { ApiGetAssignedCustomerServiceProductsAction } from '@sales/states/customer-service-product/customer-service-product.actions';
import { ApiGetFamilyAction } from '@sales/states/family/family.actions';
import {
  AuthorizedShoppingCartAction,
  CreateAuthorizedShoppingCartAction,
} from '@sales/states/order-capture/order-capture.actions';
import { catchError, tap } from 'rxjs/operators';
import { SetExistingMbbSubscriptionsAction } from '../../mbb/states/customer-product-mbb.actions';
import { ApiGetEquipmentUpgradeAgreementsForCustomerAction } from '../../mobilephone/states/mobilbytte/mobilbytte.actions';
import { CustomerEquipment } from '../models/customer-equipment';
import { CustomerResponse } from '../models/customer-response.model';
import { CustomerStateModel } from '../models/customer.model';
import { DeliveryAddress, PrivateCustomer } from '../models/private-customer.model';
import { SubscriptionType } from '../models/subscription-type';
import { CustomerHelperService } from '../services/customer-helper.service';
import { CustomerService } from '../services/customer.service';
import {
  ApiGetExistingSubscriptionsAction,
  ApiGetExistingSubscriptionsErrorAction,
  ApiGetExistingSubscriptionsSuccessAction,
  ApiGetInFlightOrdersForCustomerAction,
  ApiGetInFlightOrdersForCustomerFailedAction,
  ApiGetInFlightOrdersForCustomerSuccessAction,
  ChangeCustomerAction,
  CustomerCreditCheckAction,
  CustomerDownpaymentCheckAction,
  CustomerResetAction,
  CustomerSignatureUpdateAction,
  DownpaymentFailedAction,
  DownpaymentSuccessAction,
  ExistingCustomerCreditCheckAction,
  ExistingCustomerCreditCheckFailedAction,
  ExistingCustomerCreditCheckSuccessAction,
  ExistingCustomerEquipmentAction,
  ExistingCustomerEquipmentFailedAction,
  ExistingCustomerEquipmentSuccessAction,
  ExistingCustomerFoundAction,
  GetBankIdVerificationStatusAction,
  GetBankIdVerificationStatusExistingCustomerAction,
  GetBankIdVerificationStatusNewCustomerAction,
  GetBankIdVerificationStatusOrderDetailsAction,
  GetCustomerAction,
  GetCustomerErrorAction,
  GetCustomerSuccessAction,
  InitiateBankIdVerification,
  ReinitiateBankIdVerificationAction,
  NewCustomerCreditCheckFailedAction,
  NewCustomerCreditCheckSuccessAction,
  ResetBankIdVerificationAction,
  SetCustomerCreditCheckErrorAction,
  SetCustomerToInitialStateAction,
  SetSelectedFromCustomerSearchAction,
  UpdateCustomerEmailAction,
  UpdateCustomerInfoFromCreditcheck,
  UpdateCustomerPhoneAction,
  UpdateCustomerSubscriptionsAction,
  UpdateDeliveryAddressAction,
  UpdateInvoiceTypeAction,
  AddAuthenticatedBankIdSessionAction,
  SetCustomerAuthenticatedInMannyAction,
  CreateAuthenticatedVerificationErrorAction,
} from './customer.actions';
import { OrderCaptureStateModel } from '@app/sales/states/order-capture/order-capture.state';

const initialCustomerState = {
  customerId: '',
  changeCustomer: '',
  invoiceType: salesConstants.invoiceTypes.paper,
  consentChecked: false,
  isExistingCustomer: false,
  selectedFromCustomerSearch: false,
  hasSecretNumber: false,
  authorizeResult: new AuthorizeResult(),
  privateCustomer: new PrivateCustomer(),
  isLoadingCustomer: false,
  isLoadingProducts: false,
  isLoadingCustomerDownPaymentCheck: false,
  isLoadingCustomerCreditCheck: false,
  isLoadingInFlightOrders: false,
  hasPerformedAllowanceCheck: false,
  hasPerformedDetailedCreditCheck: false,
  inFlightOrders: [],
  customerError: '',
  customerEquipment: [],
  bankIdVerification: new BankIdVerification(),
} as CustomerStateModel;

enum Messages {
  AllowanceTimeoutError = 'Noe gikk galt ved henting av kundedetaljer, prøv igjen senere.',
  CustomerError = 'Feil ved henting av kunde',
  CustomerDetailsError = 'Noe gikk galt ved henting av kundedetaljer.',
  CustomerCreationInProgress = 'Kundeopprettelse pågår, prøv igjen senere.',
  NameSsnMismatchError = 'Fødselsnummeret eller navn samsvarer ikke med valgt kunde.',
  CreditCheckTimeOutError = 'Noe gikk galt ved Kredittsjekk, prøv igjen senere.',
  CreditCheckError = 'Kredittsjekk feilet',
  SsnMismatchError = 'Fødselsnummeret samsvarer ikke med valgt kunde.',
  CustomerSubscriptionsError = 'Error when getting customer subscriptions',
  InFlightOrderError = 'Feil ved henting av in-flight ordre for valgt kunde',
  customerEquipmentError = 'Feil ved henting av utstyr registrert på kunde',
}

@State<CustomerStateModel>({
  name: 'customer',
  defaults: initialCustomerState,
})
@Injectable()
export class CustomerState {
  private notificationService = inject(NotificationService);
  private appInsights = inject(AppInsightsService);
  private customerService = inject(CustomerService);
  private customerHelperService = inject(CustomerHelperService);
  private verificationService = inject(VerificationService);
  private router = inject(Router);
  private store = inject(Store);

  @Action(InitiateBankIdVerification)
  public initiateBankIdVerification(
    { getState, patchState }: StateContext<CustomerStateModel>,
    { msisdn, email }: InitiateBankIdVerification
  ) {
    return this.verificationService.initiateVerification(msisdn, email).pipe(
      tap(response => {
        patchState({
          bankIdVerification: {
            ...getState().bankIdVerification,
            id: response.id,
            verificationLinkCode: response.verificationLinkCode,
            status: BankIdVerificationStatus.NotAuthenticated,
          },
        });
        this.trackBankIdInitiated(response.verificationLinkCode);
      }),
      catchError(error => {
        this.notificationService.error('Kunne ikke starte BankID verifisering. Vennligst prøv på nytt.');
        throw error;
      })
    );
  }

  @Action(ReinitiateBankIdVerificationAction)
  public reinitiateBankIdVerificationAction(
    { getState, patchState }: StateContext<CustomerStateModel>,
    { orderServiceId, msisdn, email }: ReinitiateBankIdVerificationAction
  ) {
    return this.verificationService.reinitiateVerification(orderServiceId, msisdn, email).pipe(
      tap(response => {
        patchState({
          bankIdVerification: {
            ...getState().bankIdVerification,
            id: response.id,
            verificationLinkCode: response.verificationLinkCode,
            status: BankIdVerificationStatus.NotAuthenticated,
          },
        });
        this.trackBankIdInitiated(response.verificationLinkCode);
      })
    );
  }

  @Action(ResetBankIdVerificationAction)
  public resetBankIdVerification({ patchState }: StateContext<CustomerStateModel>) {
    patchState({
      bankIdVerification: new BankIdVerification(),
    });
  }

  @Action(GetBankIdVerificationStatusAction)
  public getBankIdVerificationStatus({ getState, dispatch }: StateContext<CustomerStateModel>) {
    if (getState().isExistingCustomer) {
      dispatch(new GetBankIdVerificationStatusExistingCustomerAction());
    } else {
      dispatch(new GetBankIdVerificationStatusNewCustomerAction());
    }
  }

  @Action(GetBankIdVerificationStatusNewCustomerAction)
  public getBankIdVerificationStatusNewCustomer({ getState, patchState, dispatch }: StateContext<CustomerStateModel>) {
    const request = new BankIdStatusRequest(getState().bankIdVerification.id);

    return this.verificationService.getVerificationStatus(request).pipe(
      tap(({ fullname, ssn, status, customerId }: BankIdStatusResponse) => {
        if (status === BankIdVerificationStatus.NotAuthenticated) return;
        const { status: previousStatus, verificationLinkCode } = getState().bankIdVerification;
        const isCreditChecked = !!this.store.selectSnapshot<OrderCaptureStateModel>(state => state.orderCapture)
          .shoppingCart;
        let isSsnMatchingSsnInCreditCheck = true;
        let statusToSet = status;

        let firstName = '';
        let lastName = '';
        if (fullname) {
          const names = fullname.split(' ');
          firstName = names[0];
          lastName = names.length > 1 ? names.slice(1).join(' ') : '';
        }

        if (isCreditChecked) {
          isSsnMatchingSsnInCreditCheck = ssn === getState().privateCustomer.ssn;
          if (!isSsnMatchingSsnInCreditCheck) {
            statusToSet = BankIdVerificationStatus.SsnIsDifferentFromCustomerSsn;
          }
        } else if (customerId) {
          this.router.navigateByUrl(`sales/customer/${customerId}/details`);
          dispatch(new GetBankIdVerificationStatusExistingCustomerAction());
          dispatch(new UpdateCustomerAllowanceFormAction(firstName, lastName, ssn));
          return;
        } else {
          const isVerified = isSsnMatchingSsnInCreditCheck && status === BankIdVerificationStatus.Authenticated;
          const isPersonalInfoComplete = firstName && lastName && ssn;
          if (isVerified && isPersonalInfoComplete) {
            dispatch(new UpdateCustomerAllowanceFormAction(firstName, lastName, ssn));
          }
        }
        patchState({
          bankIdVerification: {
            ...getState().bankIdVerification,
            status: statusToSet,
            isSsnMatchingSsnInCreditCheck: isSsnMatchingSsnInCreditCheck,
          },
        });
        this.trackBankIdAuthenticated(previousStatus, statusToSet, verificationLinkCode);
      })
    );
  }

  @Action(GetBankIdVerificationStatusExistingCustomerAction)
  public getBankIdVerificationStatusExistingCustomer({ getState, patchState }: StateContext<CustomerStateModel>) {
    const request = new BankIdStatusRequest(getState().bankIdVerification.id, getState().customerId);

    return this.verificationService.getVerificationStatus(request).pipe(
      tap(({ status }: BankIdStatusResponse) => {
        const { status: previousStatus, verificationLinkCode } = getState().bankIdVerification;
        patchState({
          bankIdVerification: {
            ...getState().bankIdVerification,
            status,
          },
        });
        this.trackBankIdAuthenticated(previousStatus, status, verificationLinkCode);
      })
    );
  }

  @Action(GetBankIdVerificationStatusOrderDetailsAction)
  public getBankIdVerificationStatusOrderDetails(
    { getState, patchState }: StateContext<CustomerStateModel>,
    { code, customerId }: GetBankIdVerificationStatusOrderDetailsAction
  ) {
    const request = new BankIdStatusRequest(code, customerId);

    return this.verificationService.getVerificationStatus(request).pipe(
      tap(({ status }: BankIdStatusResponse) => {
        const { status: previousStatus, verificationLinkCode } = getState().bankIdVerification;
        patchState({
          bankIdVerification: {
            ...getState().bankIdVerification,
            status,
          },
        });
        this.trackBankIdAuthenticated(previousStatus, status, verificationLinkCode);
      })
    );
  }

  private trackBankIdInitiated(verificationLinkCode?: string): void {
    this.appInsights.trackEvent('BankIdVerificationInitiated', { verificationLinkCode });
  }

  private trackBankIdAuthenticated(
    previousStatus: BankIdVerificationStatus,
    newStatus: BankIdVerificationStatus,
    verificationLinkCode?: string
  ): void {
    const isAuthenticated = (status: BankIdVerificationStatus): boolean =>
      status === BankIdVerificationStatus.Authenticated || status === BankIdVerificationStatus.SsnIsSameAsCustomerSsn;

    // Only track the first time we transition into an authenticated status,
    // not on every subsequent poll while already authenticated.
    if (!isAuthenticated(newStatus) || isAuthenticated(previousStatus)) {
      return;
    }

    // durationMs is computed in Application Insights by joining this event with
    // BankIdVerificationInitiated on verificationLinkCode
    this.appInsights.trackEvent('BankIdVerificationAuthenticated', { verificationLinkCode });
  }

  @Action(UpdateCustomerSubscriptionsAction)
  public customerSet(ctx: StateContext<CustomerStateModel>, { customer }: UpdateCustomerSubscriptionsAction) {
    if (customer.isExistingCustomer) {
      ctx.dispatch(new ApiGetExistingSubscriptionsAction(customer.customerId));
    }
  }

  @Action(CustomerCreditCheckAction)
  public CustomerCreditCheckAction(
    { dispatch, patchState }: StateContext<CustomerStateModel>,
    { firstName, lastName, ssn, runDownPaymentCheck }: CustomerCreditCheckAction
  ) {
    patchState({ isLoadingCustomerCreditCheck: true });

    return dispatch(
      new CreateAuthorizedShoppingCartAction({
        firstName,
        lastName,
        ssn,
        authorizeForDownPayments: runDownPaymentCheck,
      } as CreateAuthorizedShoppingCartRequest)
    );
  }

  @Action(ExistingCustomerFoundAction)
  public customerCreditCheckSuccess(
    { dispatch, setState }: StateContext<CustomerStateModel>,
    { customerId, firstName, lastName, ssn, runDownPaymentCheck }: ExistingCustomerFoundAction
  ) {
    if (!customerId) {
      this.notificationService.error(Messages.CustomerCreationInProgress);
      setState(
        patch<CustomerStateModel>({
          isLoadingCustomerCreditCheck: false,
        })
      );
    } else {
      setState(
        patch<CustomerStateModel>({
          customerId,
          privateCustomer: patch<PrivateCustomer>({ firstname: firstName, lastname: lastName, ssn }),
        })
      );
      dispatch(new GetCustomerAction(customerId));
      dispatch(new ExistingCustomerCreditCheckAction(customerId, runDownPaymentCheck, firstName, lastName, ssn));
    }
  }

  @Action(ExistingCustomerCreditCheckAction)
  public existingPrivateCustomerCreditCheck(
    { dispatch, setState }: StateContext<CustomerStateModel>,
    { customerId, runDownPaymentCheck, firstName, lastName, ssn }: ExistingCustomerCreditCheckAction
  ) {
    setState(
      patch<CustomerStateModel>({
        isLoadingCustomerCreditCheck: true,
        authorizeResult: new AuthorizeResult(),
      })
    );

    dispatch(
      new CreateAuthorizedShoppingCartAction({
        customerNo: customerId,
        firstName,
        lastName,
        ssn,
        authorizeForDownPayments: runDownPaymentCheck,
      } as CreateAuthorizedShoppingCartRequest)
    );
  }

  @Action(ExistingCustomerCreditCheckSuccessAction)
  public existingCustomerCreditCheckSuccess(
    { setState }: StateContext<CustomerStateModel>,
    { authorizeResult, runDownPaymentCheck }: ExistingCustomerCreditCheckSuccessAction
  ) {
    setState(
      patch<CustomerStateModel>({
        isLoadingCustomerCreditCheck: false,
        hasPerformedAllowanceCheck: true,
        hasPerformedDetailedCreditCheck: runDownPaymentCheck,
        authorizeResult: patch<AuthorizeResult>({
          ...authorizeResult,
        }),
      })
    );
  }

  @Action(ExistingCustomerCreditCheckFailedAction)
  public existingCustomerCreditCheckFailed(
    { patchState }: StateContext<CustomerStateModel>,
    { error }: ExistingCustomerCreditCheckFailedAction
  ) {
    if (error?.status === HttpStatusCode.GatewayTimeout) {
      this.notificationService.error(Messages.AllowanceTimeoutError);
    } else {
      this.notificationService.error(Messages.CustomerDetailsError);
    }
    patchState({ isLoadingCustomerCreditCheck: false });
  }

  @Action(SetCustomerCreditCheckErrorAction)
  public setCustomerCreditCheckError(
    { setState }: StateContext<CustomerStateModel>,
    { message }: SetCustomerCreditCheckErrorAction
  ) {
    setState(
      patch<CustomerStateModel>({
        isLoadingCustomerCreditCheck: false,
        hasPerformedAllowanceCheck: true,
        authorizeResult: {
          subscriptionAllowance: {
            reason: message,
            approved: false,
          },
          mbbSubscriptionAllowance: {
            reason: message,
            approved: false,
          },
        } as AuthorizeResult,
      })
    );
  }

  @Action(NewCustomerCreditCheckSuccessAction)
  public creditCheckSuccess(
    { setState, dispatch }: StateContext<CustomerStateModel>,
    { authorizeResult, ssn }: NewCustomerCreditCheckSuccessAction
  ) {
    if (authorizeResult.creditCheckedEntity) {
      setState(
        patch<CustomerStateModel>({
          isLoadingCustomerCreditCheck: false,
          hasPerformedAllowanceCheck: true,
          privateCustomer: patch<PrivateCustomer>({ ssn }),
          authorizeResult: patch<AuthorizeResult>({
            ...authorizeResult,
          }),
        })
      );
      dispatch(new UpdateCustomerInfoFromCreditcheck(authorizeResult.creditCheckedEntity));
    } else {
      setState(
        patch<CustomerStateModel>({
          isLoadingCustomerCreditCheck: false,
        })
      );
    }
  }

  @Action(NewCustomerCreditCheckFailedAction)
  public newCustomerCreditCheckFailed(
    { patchState }: StateContext<CustomerStateModel>,
    { error }: NewCustomerCreditCheckFailedAction
  ) {
    if (error?.status === HttpStatusCode.GatewayTimeout) {
      this.notificationService.error(Messages.AllowanceTimeoutError);
    } else if (error?.status === HttpStatusCode.BadRequest && error?.error) {
      this.notificationService.error(error.error);
    } else {
      this.notificationService.error(Messages.CustomerDetailsError);
    }
    patchState({ isLoadingCustomerCreditCheck: false });
  }

  @Action(CustomerDownpaymentCheckAction)
  public newCustomerDownpaymentCheck(
    { dispatch, setState }: StateContext<CustomerStateModel>,
    { downpaymentPayload }: CustomerDownpaymentCheckAction
  ) {
    setState(
      patch<CustomerStateModel>({
        isLoadingCustomerDownPaymentCheck: true,
        privateCustomer: patch<PrivateCustomer>({ ssn: downpaymentPayload.ssn }),
      })
    );
    const { customerId, ssn, firstName, lastName } = downpaymentPayload;
    dispatch(
      new AuthorizedShoppingCartAction({
        customerNo: customerId,
        ssn,
        firstName,
        lastName,
        authorizeForDownPayments: true,
      } as CreateAuthorizedShoppingCartRequest)
    );
  }

  @Action(DownpaymentSuccessAction)
  public downpaymentSuccess({ setState }: StateContext<CustomerStateModel>, { authorizeResult }: DownpaymentSuccessAction) {
    setState(
      patch<CustomerStateModel>({
        isLoadingCustomerDownPaymentCheck: false,
        hasPerformedAllowanceCheck: true,
        hasPerformedDetailedCreditCheck: true,
        customerError: '',
        customerErrorCode: null,
        authorizeResult: patch<AuthorizeResult>({
          ...authorizeResult,
        }),
      })
    );
  }

  @Action(DownpaymentFailedAction)
  public downpaymentFailed({ patchState, setState }: StateContext<CustomerStateModel>, { error }: DownpaymentFailedAction) {
    if (error?.status === HttpStatusCode.GatewayTimeout) {
      this.notificationService.error(Messages.CreditCheckTimeOutError);
    } else if (error instanceof ProblemExistingCustomerIdAndSsnMismatch) {
      setState(
        patch<CustomerStateModel>({
          isLoadingCustomerDownPaymentCheck: false,
          customerError: Messages.NameSsnMismatchError,
          customerErrorCode: CustomerErrorCode.SSN_MISMATCH,
        })
      );
    } else if (error?.status === HttpStatusCode.BadRequest && error?.error) {
      this.notificationService.error(error.error);
    } else {
      this.notificationService.error(Messages.CreditCheckError);
    }
    patchState({ isLoadingCustomerDownPaymentCheck: false });
  }

  @Action(ChangeCustomerAction)
  public ChangeCustomerAction(ctx: StateContext<CustomerStateModel>, action: ChangeCustomerAction) {
    return ctx.patchState({ changeCustomer: action.changeCustomer });
  }

  @Action(CustomerSignatureUpdateAction)
  public customerSignatureUpdate(ctx: StateContext<CustomerStateModel>, action: CustomerSignatureUpdateAction) {
    ctx.setState(
      patch({
        privateCustomer: patch({
          identificationType: action.customer.privateCustomer.identificationType,
          identificationNumber: action.customer.privateCustomer.identificationNumber,
        }),
      })
    );
  }

  @Action(CustomerResetAction)
  public customerReset(ctx: StateContext<CustomerStateModel>) {
    ctx.setState(initialCustomerState);
  }

  @Action(GetCustomerAction)
  public getCustomer({ patchState, dispatch }: StateContext<CustomerStateModel>, { customerId }: GetCustomerAction) {
    patchState({ isLoadingCustomer: true });
    dispatch([new ApiGetInFlightOrdersForCustomerAction(customerId), new ApiGetFamilyAction(customerId)]);

    return this.customerService.get(customerId).pipe(
      tap((response: CustomerResponse) => {
        response.phone = response.phone?.trim() ?? response.phone;
        dispatch(new GetCustomerSuccessAction(response));
      }),
      catchError(error => dispatch(new GetCustomerErrorAction(error)))
    );
  }

  @Action(SetCustomerToInitialStateAction)
  public SetCustomerToInitialStateAction({ getState, dispatch }: StateContext<CustomerStateModel>) {
    const { phone, email } = getState().initialCustomerInfo;
    dispatch([new UpdateCustomerEmailAction(email), new UpdateCustomerPhoneAction(phone)]);
  }

  @Action(GetCustomerSuccessAction)
  public getCustomerSuccessAction(
    { patchState, dispatch, getState }: StateContext<CustomerStateModel>,
    { response }: GetCustomerSuccessAction
  ) {
    const { privateCustomer, authorizeResult } = getState();
    const customer = new CustomerStateModel();
    customer.authorizeResult = authorizeResult;
    customer.isExistingCustomer = true;
    customer.customerId = response.id;

    customer.initialCustomerInfo.phone = response.phone;
    customer.initialCustomerInfo.email = response.email;

    this.setPrivateCustomer(customer, response, privateCustomer);

    patchState({ isLoadingCustomer: false, ...customer });
    dispatch([
      new UpdateCustomerSubscriptionsAction(customer),
      new ApiGetEquipmentUpgradeAgreementsForCustomerAction(customer.customerId),
      new ApiGetAssignedCustomerServiceProductsAction(customer.customerId),
      new ExistingCustomerEquipmentAction(customer.customerId),
    ]);
  }

  private setPrivateCustomer(
    customer: CustomerStateModel,
    response: CustomerResponse,
    existingPrivateCustomerInfo: PrivateCustomer
  ) {
    customer.privateCustomer.birthdate = dateFns.DateStringToDate(response.customerIdentifier);
    customer.privateCustomer.fullname = response.fullName;
    customer.privateCustomer.address = transformAddress(response.legalAddress);

    customer.privateCustomer.city = response.legalAddress.city;
    customer.privateCustomer.postCode = response.legalAddress.zipCode;
    customer.privateCustomer.phone = response.phone;
    customer.privateCustomer.email = response.email;
    customer.privateCustomer.ssn = existingPrivateCustomerInfo?.ssn;
    customer.invoiceType = response.invoiceType;
    if (response.fullName) {
      customer.privateCustomer.firstname = existingPrivateCustomerInfo?.firstname;
      customer.privateCustomer.lastname = existingPrivateCustomerInfo?.lastname;
      const { firstName, lastName } = this.customerHelperService.splitFullName(response.fullName);
      if (!customer.privateCustomer.firstname && firstName) {
        customer.privateCustomer.firstname = firstName;
      }
      if (!customer.privateCustomer.lastname && lastName) {
        customer.privateCustomer.lastname = lastName;
      }
    }

    customer.privateCustomer.deliveryAddress = {
      firstName: customer.privateCustomer.firstname,
      lastName: customer.privateCustomer.lastname,
      address: customer.privateCustomer.address,
      postCode: customer.privateCustomer.postCode,
      city: customer.privateCustomer.city,
    } as DeliveryAddress;
  }

  @Action(GetCustomerErrorAction)
  public GetCustomerErrorAction({ patchState }: StateContext<CustomerStateModel>, {}: GetCustomerErrorAction) {
    this.notificationService.error(Messages.CustomerError);
    patchState({ isLoadingCustomer: false });
  }

  @Action(UpdateCustomerInfoFromCreditcheck)
  public updatePrivateCustomer(
    { setState }: StateContext<CustomerStateModel>,
    { creditCheckPerson }: UpdateCustomerInfoFromCreditcheck
  ) {
    setState(
      patch({
        privateCustomer: patch<PrivateCustomer>({
          firstname: creditCheckPerson.firstName,
          lastname: creditCheckPerson.lastName,
          fullname: `${creditCheckPerson.firstName} ${creditCheckPerson.lastName}`,
          address: creditCheckPerson.address,
          city: creditCheckPerson.city,
          postCode: creditCheckPerson.zip,
          birthdate: creditCheckPerson.birthDate,
          deliveryAddress: {
            firstName: creditCheckPerson.firstName,
            lastName: creditCheckPerson.lastName,
            address: creditCheckPerson.address,
            postCode: creditCheckPerson.zip,
            city: creditCheckPerson.city,
          } as DeliveryAddress,
        }),
      })
    );
  }

  @Action(UpdateCustomerEmailAction)
  public updateCustomerEmail({ setState }: StateContext<CustomerStateModel>, { email }: UpdateCustomerEmailAction) {
    setState(patch({ privateCustomer: patch<PrivateCustomer>({ email: email }) }));
  }
  @Action(UpdateCustomerPhoneAction)
  public updateCustomerPhone({ setState }: StateContext<CustomerStateModel>, { phone }: UpdateCustomerPhoneAction) {
    setState(patch({ privateCustomer: patch<PrivateCustomer>({ phone: phone }) }));
  }
  @Action(UpdateInvoiceTypeAction)
  public updateInvoiceType({ patchState }: StateContext<CustomerStateModel>, { invoiceType }: UpdateInvoiceTypeAction) {
    patchState({ invoiceType });
  }

  @Action(ApiGetExistingSubscriptionsAction)
  public getSubscriptions(
    { dispatch, patchState }: StateContext<CustomerStateModel>,
    action: ApiGetExistingSubscriptionsAction
  ) {
    patchState({ isLoadingProducts: true });
    return this.customerService.getSubscriptions(action.customerId).pipe(
      tap(
        response => dispatch(new ApiGetExistingSubscriptionsSuccessAction(response)),
        () => dispatch(new ApiGetExistingSubscriptionsErrorAction(Messages.CustomerSubscriptionsError))
      )
    );
  }

  @Action(ApiGetExistingSubscriptionsSuccessAction)
  public getSubscriptionsResponse(
    { dispatch, patchState }: StateContext<CustomerStateModel>,
    { subscriptions }: ApiGetExistingSubscriptionsSuccessAction
  ) {
    const existingVoiceProducts = subscriptions.filter(s => s.subscriptionFamily?.productType === SubscriptionType.Voice);
    const existingMbbProducts = subscriptions.filter(s => s.subscriptionFamily?.productType === SubscriptionType.MBB);
    if (existingVoiceProducts.length > 0) {
      dispatch(new SetExistingVoiceSubscriptionsAndAddNewSubscriptionsAction(existingVoiceProducts));
    }
    if (existingMbbProducts.length > 0) {
      dispatch(new SetExistingMbbSubscriptionsAction(existingMbbProducts));
    }
    patchState({ isLoadingProducts: false });
  }

  @Action(SetSelectedFromCustomerSearchAction)
  public setSelectedFromCustomerSearch({ patchState }: StateContext<CustomerStateModel>) {
    patchState({ selectedFromCustomerSearch: true });
  }

  @Action(ApiGetExistingSubscriptionsErrorAction)
  public getSubscriptionResponseError(
    { patchState }: StateContext<CustomerStateModel>,
    action: ApiGetExistingSubscriptionsErrorAction
  ) {
    this.notificationService.error(action.error);
    patchState({ isLoadingProducts: false });
  }

  @Action(ApiGetInFlightOrdersForCustomerAction)
  public getInFlightOrders(
    { patchState, dispatch }: StateContext<CustomerStateModel>,
    { customerId }: ApiGetInFlightOrdersForCustomerAction
  ) {
    patchState({ isLoadingInFlightOrders: true });

    return this.customerService.getInFlightOrdersForCustomer(customerId).pipe(
      tap((inFlightOrders: InFlightOrder[]) => dispatch(new ApiGetInFlightOrdersForCustomerSuccessAction(inFlightOrders))),
      catchError(() => dispatch(new ApiGetInFlightOrdersForCustomerFailedAction()))
    );
  }

  @Action(ApiGetInFlightOrdersForCustomerFailedAction)
  public getInFlightOrderForCustomerFailed({ patchState }: StateContext<CustomerStateModel>) {
    patchState({ isLoadingInFlightOrders: false });

    this.notificationService.error(Messages.InFlightOrderError);
  }

  @Action(ApiGetInFlightOrdersForCustomerSuccessAction)
  public getInFlightOrdersSuccess(
    { patchState }: StateContext<CustomerStateModel>,
    { inFlightOrders }: ApiGetInFlightOrdersForCustomerSuccessAction
  ) {
    patchState({
      inFlightOrders,
      isLoadingInFlightOrders: false,
    });
  }

  @Action(UpdateDeliveryAddressAction)
  public updateDeliveryAddress(
    { setState, getState }: StateContext<CustomerStateModel>,
    { deliveryAddress }: UpdateDeliveryAddressAction
  ) {
    const { privateCustomer } = getState();
    setState(
      patch<CustomerStateModel>({
        privateCustomer: { ...privateCustomer, deliveryAddress: deliveryAddress },
      })
    );
  }

  @Action(ExistingCustomerEquipmentAction)
  public existingCustomerEquipment(
    { dispatch }: StateContext<CustomerStateModel>,
    { customerId }: ExistingCustomerEquipmentAction
  ) {
    return this.customerService.getEquipment(customerId).pipe(
      tap((customerEquipment: CustomerEquipment[]) =>
        dispatch(new ExistingCustomerEquipmentSuccessAction(customerEquipment))
      ),
      catchError(() => dispatch(new ExistingCustomerEquipmentFailedAction()))
    );
  }

  @Action(ExistingCustomerEquipmentSuccessAction)
  public existingCustomerEquipmentSuccess(
    { patchState }: StateContext<CustomerStateModel>,
    { customerEquipment }: ExistingCustomerEquipmentSuccessAction
  ) {
    patchState({ customerEquipment: customerEquipment });
  }

  @Action(ExistingCustomerEquipmentFailedAction)
  public existingCustomerEquipmentFailed({}: StateContext<CustomerStateModel>) {
    this.notificationService.error(Messages.customerEquipmentError);
  }

  @Action(AddAuthenticatedBankIdSessionAction)
  public addAuthenticatedBankIdSession(
    { patchState, getState }: StateContext<CustomerStateModel>,
    { id, verificationLinkCode }: AddAuthenticatedBankIdSessionAction
  ) {
    const { status: previousStatus } = getState().bankIdVerification;
    patchState({
      bankIdVerification: {
        ...getState().bankIdVerification,
        id,
        verificationLinkCode,
        status: BankIdVerificationStatus.Authenticated,
        isAuthenticatedFromManny: true,
      },
    });
    this.trackBankIdAuthenticated(previousStatus, BankIdVerificationStatus.Authenticated, verificationLinkCode);
  }

  @Action(CreateAuthenticatedVerificationErrorAction)
  public createAuthenticatedVerificationError({ patchState, getState }: StateContext<CustomerStateModel>) {
    patchState({
      bankIdVerification: {
        ...getState().bankIdVerification,
        isAuthenticatedFromManny: false,
      },
    });
  }

  @Action(SetCustomerAuthenticatedInMannyAction)
  public setCustomerAuthenticatedInManny({ patchState, getState }: StateContext<CustomerStateModel>) {
    patchState({
      bankIdVerification: {
        ...getState().bankIdVerification,
        isAuthenticatedFromManny: true,
      },
    });
  }
}
